/* eslint-disable no-param-reassign, max-lines-per-function, max-lines */
import { createStore } from "vuex";
import { pluralize } from "common/utils/format/word";
import { api, routes, resolveOptions } from "jiffy-api";
import { mergeDeep, isObject } from "common/utils/core";
import { PlainError } from "common/utils/errors";
import { isMobile, redirectTo } from "common/utils/browser";
import { objNamesToCamelCase, camelize, objNamesToSnakeCase } from "common/utils/helpers/cases";
import * as flag from "common/utils/flag";
import { changeActiveCartName } from "components/jiffy/masthead-cart/masthead-cart";
import { track, registerMixpanel } from "jiffy-analytics";
import { storage } from "common/utils/storage";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import scrollToAlerts, { scrollToTop } from "./helpers/sroll-to-alerts";
import plugins from "./plugins";
import digitalAssetsModule from "./modules/digital-assets-module";

const $getCsrfMeta = () => document.querySelector('meta[name="csrf-token"]');
const obtainCsrfToken = () => $getCsrfMeta()?.getAttribute("content");

const initialState = {
  cartConfig: {
    orderNumber: null,
    storeCode: "us",
    isMcuCart: false,
    crafterpayAvailable: false,
    isCrafterOnboarded: false,
    productSuggestions: false,
    j1Cart: false,
    checkoutLoading: false,
    inventoryLoading: false,
    couponLoading: false,
    deliveryLoading: false,
    locationLoading: false,
    removingItem: false,
    refreshLoading: false,
    isIncorrectAddress: false,
    isAccountPresent: false,
    shippingTexts: {},
    isNamedCartsAvailable: false,
    isNamedCartsManagementAvailable: false,
    isNamedCartsCustomerAvailable: false,
    isNamedCartsVisible: false,
    isStandardDeliveryInfoVisible: false,
    showSaveCartModal: false,
    cartName: "",
    notes: "",
    customer: {},
    guestZipcode: "",
    autoCartType: null,
    createAccountURL: `/signup?redirect_to=${encodeURIComponent(window.location.href)}`,
    fgWithNonFastestOption: ["4"], // [Port & Company]
    csrfToken: obtainCsrfToken(),
    dtfPromotionBlockVisible: false,
    digitalPromotionBlockVisible: false,
    dtfCouponApplied: false,
    madeShippingRule: null,
    dtfShippingRule: null,
    digitalShippingRule: null,
    imageServiceShippingRule: null,
    spangleShippingRule: null,
    onlyDigitalAssetItems: false
  },
  cartHeader: {
    clearButtonInformation: {}
  },
  accountAddresses: {
    addresses: null,
    shipAddressId: null
  },
  experiments: {},
  itemsOutOfStock: {},
  checkoutButtons: {},
  couponCode: {
    appliedCouponCode: "",
    couponDiscount: 0.0,
    freeShipping: false
  },
  orderGroups: {},
  subOrdersSummary: {},
  subOrdersFreeShipping: {},
  subOrdersHasJiffyMade: false,
  notifications: [],
  deliveryNotification: {
    mixed: {
      deliveryDate: null,
      isPreOrder: false,
      isMultipleDeliveries: false,
      isShippingToday: false
    },
    mixedFastest: {
      deliveryDate: null,
      isPreOrder: false,
      isMultipleDeliveries: false,
      isShippingToday: false
    }
  },
  summary: {
    isRetailPriceCorrect: true,
    retailSavings: 0.0,
    productsSubtotal: 0.0,
    totalItems: 0,
    total: 0.0,
    taxTotal: 0.0,
    retailTotal: 0.0,
    shippingUrl: "",
    bulkDiscount: 0.0,
    saleDiscount: 0.0,
    couponDiscount: 0.0,
    currencyCode: "USD",
    countryCode: "US",
    groups: []
  },
  zipcode: {},
  storeCredit: {
    amount: 0.0,
    isVisible: false,
    isUsed: false,
    closestToExpire: {}
  },
  specificStoreCredits: {},
  location: {},
  locationAddresses: [],
  saleImprovedShippingPriceDetails: {},
  shippingInfo: {
    saleShippingChargeShown: false,
    shippingSaleEndDate: null,
    freeShippingOver: null,
    underShippingPrice: null
  },
  shipments: {
    groupIds: [],
    titles: {},
    orders: {},
    totalSurcharges: {},
    totalShipping: {}
  },
  delivery: {
    subOrdersDeliveryDates: {},
    deliveryDates: {
      mixed: [],
      mixedFastest: []
    },
    sortedShipments: []
  },
  deliveryWarehouses: [],
  crafterpayQuote: {},
  mobileApplePayVersion: null,
  stripeFinancingSupported: false,
  modalState: {
    shownZipcodeModal: false,
    showStickyCheckout: false
  },
  savedCarts: {
    carts: [],
    active: null,
    hidden: false,
    error: null,
    isLoading: false,
    isUpdating: false,
    isSwitching: false,
    isCreating: false,
    isLoaded: false,
    isAttaching: false,
    isDetaching: false,
    isCustomerCreating: false,
    isCustomerChoosing: false,
    showAnnouncement: false,
    showDateAnnouncement: false,
    showDateTooltip: false,
    showDateTooltipOnce: flag.isOff("__JIFFY__VUE__MULTI_CARTS_DATE_TOOLTIP__")
  },
  etsy: {
    observationPeriod: null,
    platformName: null,
    unmappedEtsyLineItemsByListing: []
  },
  platform: {
    appPlatform: window.MOBILE_APP_PLATFORM,
    env: window.jiffy?.env,
    host: window.jiffy?.host,
    isApp: window.MOBILE_APP,
    isMobile: isMobile()
  },
  usp: {
    bulkDiscountAt: "$99",
    dtfFreeShippingThreshold: "29",
    freeShippingAt: "$59",
    freeReturnsFor: "100"
  },
  dtf: {
    isRetrying: false,
    showModal: false,
    actionCableConnected: true
  },
  dtfPricing: {},
  lastCompletedOrderPaymentMethodSubtype: "",
  shipMethodChangeGroupId: null,
  initialDeliveryDateMixpanelData: {
    isSent: false,
    earliestTimeToGetDate: null,
    earliestTimeToRenderDate: null,
    cartTimeToGetDate: null,
    cartTimeToRenderDate: null
  },
  assignmentDuration: null,
  changeDeliveryDateMixpanelData: {
    earliestExpectedDeliveryDate: null,
    latestExpectedDeliveryDate: null,
    previousEarliestDate: null,
    previousLatestDate: null,
    earliestTimeToGetDate: null,
    earliestTimeToRenderDate: null,
    cartTimeToGetDate: null,
    cartTimeToRenderDate: null
  },
  changeCartItemStartTime: 0,
  damCrossSell: {
    order: [],
    content: {}
  },
  preCutConfig: {},
  reviewsData: {
    sticker: {
      avgRating: null,
      reviewsCount: null
    }
  },
  storeCreditsByBusinessLines: false,
  expectedTransfersStoreCredit: null,
  fullyPayingWithStoreCredits: false,
  cartSubtotalAfterStoreCredits: 0,
  deliveryLoadingCounter: 0
};

const store = createStore({
  state() {
    return {
      cartState: { ...initialState }
    };
  },
  mutations: {
    init(state, initialCart) {
      state.cartState = mergeDeep(state.cartState, objNamesToCamelCase(initialCart));
    },
    pushNotification(state, notification) {
      state.cartState.notifications = [...state.cartState.notifications, notification];
    },
    saveConfigCitySelected(state, status) {
      state.cartState.cartConfig.citySelected = status;
    },
    removeNotification(state, id) {
      state.cartState.notifications = state.cartState.notifications.filter(item => item.id !== id);
    },
    replaceCartState(state, newCartState) {
      const newState = objNamesToCamelCase(newCartState);
      state.cartState = {
        ...state.cartState,
        ...newState,
        cartConfig: {
          ...state.cartState.cartConfig,
          ...newState.cartConfig
        },
        summary: {
          ...state.cartState.summary,
          ...newState.summary
        }
      };
    },
    setCheckoutLoadingStatus(state, status) {
      state.cartState.cartConfig.checkoutLoading = status;
    },
    setInventoryLoadingStatus(state, status) {
      state.cartState.cartConfig.inventoryLoading = status;
    },
    setCouponLoadingStatus(state, status) {
      state.cartState.cartConfig.couponLoading = status;
    },
    setDeliveryLoadingStatus(state, status) {
      if (status) {
        state.cartState.deliveryLoadingCounter += 1;
      } else {
        state.cartState.deliveryLoadingCounter = Math.max(
          0,
          state.cartState.deliveryLoadingCounter - 1
        );
      }
      state.cartState.cartConfig.deliveryLoading = state.cartState.deliveryLoadingCounter > 0;
    },
    setRemovingItem(state, itemId) {
      state.cartState.cartConfig.removingItem = itemId;
    },
    setSystemError(state, text = "System error. Please try again later.") {
      state.cartState.cartConfig.checkoutLoading = false;
      state.cartState.cartConfig.couponLoading = false;

      if (text) {
        state.cartState.notifications = [
          {
            type: "error",
            text
          }
        ];
        throw new PlainError(text);
      }
    },
    setCurrentNotification(state, notification) {
      state.cartState.notifications = [notification];
    },
    getCurrentLocationSuccess(state, location) {
      state.cartState.location = location;
    },
    resetLocation(state) {
      state.cartState.location = initialState.location;
      state.cartState.shipments = initialState.shipments;
      state.cartState.delivery = initialState.delivery;
    },
    changeAddressSuccess(state, id) {
      state.cartState.accountAddresses.shipAddressId = id;
    },
    setLocationLoadingStatus(state, status) {
      state.cartState.cartConfig.locationLoading = status;
    },
    setRefreshLoading(state, status) {
      state.cartState.cartConfig.refreshLoading = status;
    },
    setSelectedShipmentOption(state, { category, groupId }) {
      const groupShipments = state.cartState.shipments.orders[groupId];
      let selectedOption;

      groupShipments.options = groupShipments.options.map(option => {
        if (option.category === category || camelize(option.category) === category) {
          selectedOption = option;
          option.selected = true;
        } else {
          option.selected = false;
        }
        return option;
      });

      // Standard orders would have already set the selected option from above.
      // Though for MCU orders, the selected shipping category could not be available.
      // Example:
      //   1. Selecting 'quick' but 'quick' is only avaulable for Port & Company FG.
      //   2. Selecting 'economy' but FG orders doesn't offer the 'economy' option.
      // As the 'fastest' option is always available for all orders, standard and FG orders,
      // If the customer selected a shipping category other than 'fastest',
      // then the customer is most probably considering cost over delivery dates.
      // Hence, we offer the cheapest MCU shipping option possible.
      if (!selectedOption) {
        const lastOption = groupShipments.options[groupShipments.options.length - 1];
        lastOption.selected = true;
      }
    },
    setShipMethodChangeGroupId(state, { groupId }) {
      state.cartState.shipMethodChangeGroupId = groupId;
    },
    setIsCrafterOnboarded(state, isOnboarded) {
      state.cartState.cartConfig.isCrafterOnboarded = isOnboarded;
    },
    setSavedCartsLoading(state, isLoading) {
      state.cartState.savedCarts.isLoading = isLoading;
    },
    setSavedCartsUpdating(state, isUpdating) {
      state.cartState.savedCarts.isUpdating = isUpdating;
    },
    setSavedCartsSwitching(state, isSwitching) {
      state.cartState.savedCarts.isSwitching = isSwitching;
    },
    setSavedCartsCreating(state, isCreating) {
      state.cartState.savedCarts.isCreating = isCreating;
    },
    setSavedCartsError(state, error) {
      state.cartState.savedCarts.error = error;
    },
    setSavedCarts(state, { carts, active }) {
      state.cartState.savedCarts.carts = carts;
      state.cartState.savedCarts.isLoaded = true;
      if (active) {
        state.cartState.savedCarts.active = active;
      }
    },
    setSavedCartsUpdatedAt(state, updatedAt) {
      state.cartState.cartConfig.updatedAt = updatedAt;
    },
    setSavedCartsAnnouncement(state, visible) {
      state.cartState.savedCarts.showAnnouncement = visible;
    },
    setSavedCartsDateAnnouncement(state, visible) {
      state.cartState.savedCarts.showDateAnnouncement = visible;
    },
    setSavedCartsDateTooltip(state, visible) {
      state.cartState.savedCarts.showDateTooltip = visible;
    },
    setSavedCartsDateTooltipOnce(state) {
      state.cartState.savedCarts.showDateTooltipOnce = false;
    },
    setSavedCartsName(state, name) {
      state.cartState.cartConfig.cartName = name;
    },
    setOrderNumber(state, number) {
      state.cartState.cartConfig.orderNumber = number;
    },
    setSavedCartsCustomerAttaching(state, isAttaching) {
      state.cartState.savedCarts.isAttaching = isAttaching;
    },
    setSavedCartsCustomer(state, customer) {
      state.cartState.cartConfig.customer = customer;
    },
    setSavedCartsCustomerDetaching(state, isAttaching) {
      state.cartState.savedCarts.isDetaching = isAttaching;
    },
    setSavedCartsCustomerCreating(state, isCustomerCreating) {
      state.cartState.savedCarts.isCustomerCreating = isCustomerCreating;
    },
    setSavedCartsCustomerChoosing(state, isCustomerChoosing) {
      state.cartState.savedCarts.isCustomerChoosing = isCustomerChoosing;
    },
    setGuestZipcode(state, zipcode) {
      state.cartState.cartConfig.guestZipcode = zipcode;
    },
    setUnmappedEtsyLineItemsByListing(state, itemsByVariant) {
      state.cartState.etsy.unmappedEtsyLineItemsByListing = itemsByVariant;
    },
    setPlatformName(state, platformName) {
      state.cartState.etsy.platformName = platformName;
    },
    setEtsyObservationPeriod(state, period) {
      state.cartState.etsy.observationPeriod = period;
    },
    toggleZipcodeModalVisibility(state, callOriginId) {
      state.cartState.modalState.shownZipcodeModal = !state.cartState.modalState.shownZipcodeModal;

      if (callOriginId) {
        state.cartState.modalState.callOriginId = callOriginId;
      }
    },
    toggleCheckoutModalVisibility(state, show) {
      state.cartState.modalState.showStickyCheckout =
        show === undefined ? !state.cartState.modalState.showStickyCheckout : show;
    },
    updateCouponDiscount(state, discount) {
      state.couponCode.couponDiscount = discount;
    },
    setInitialDeliveryDateMixpanelData(state, mixpanelPartialData) {
      state.cartState.initialDeliveryDateMixpanelData = {
        ...state.cartState.initialDeliveryDateMixpanelData,
        ...mixpanelPartialData
      };
    },
    setChangeDeliveryDateMixpanelData(state, mixpanelPartialData) {
      state.cartState.changeDeliveryDateMixpanelData = {
        ...state.cartState.changeDeliveryDateMixpanelData,
        ...mixpanelPartialData
      };
    },
    setChangeCartItemStartTime(state, value) {
      state.cartState.changeCartItemStartTime =
        value === undefined ? performance?.now() || 0 : value;
    },
    updateStoreCreditUsageState(state, value) {
      state.cartState.storeCredit.isUsed = value;
    },
    updateSpecificStoreCreditUsageState(state, { businessLine, isUsed }) {
      state.cartState.specificStoreCredits[businessLine] ||= {};
      state.cartState.specificStoreCredits[businessLine].isUsed = isUsed;
    },
    setAssignmentDuration(state, value) {
      state.cartState.assignmentDuration = value;
    },
    // We have 2 vuex store that uses DtfProgressBar, this mutation is used on
    // the other store but not here. Mocking it to avoid errors.
    updateArtworkValues(_state, _value) {}
  },
  getters: {
    // # Config
    csrfToken: state => state.cartState.cartConfig.csrfToken,
    fgWithNonFastestOption: state => state.cartState.cartConfig.fgWithNonFastestOption,
    createAccountURL: state => state.cartState.cartConfig.createAccountURL,
    currentLocationLoading: state => state.cartState.cartConfig.locationLoading,
    storeCode: state => state.cartState.cartConfig.storeCode,
    isUs: state => state.cartState.cartConfig.storeCode === "us",
    storeCurrency: (state, getters) => (getters.isUs ? "$" : "CA$"),
    accountPresent: state => state.cartState.cartConfig.isAccountPresent,
    cartBulkDiscount: state => parseFloat(state.cartState.summary.bulkDiscount),
    initCitySelected: state => state.cartState.cartConfig.citySelected,
    isMcuCart: state => state.cartState.cartConfig.isMcuCart,
    checkoutLoading: state => state.cartState.cartConfig.checkoutLoading,
    inventoryLoading: state => state.cartState.cartConfig.inventoryLoading,
    j1Cart: state => state.cartState.cartConfig.j1Cart,
    couponLoading: state => state.cartState.cartConfig.couponLoading,
    deliveryLoading: state => state.cartState.cartConfig.deliveryLoading,
    refreshLoading: state => state.cartState.cartConfig.refreshLoading,
    removingItem: state => state.cartState.cartConfig.removingItem,
    assignmentDuration: state => state.cartState.assignmentDuration,
    dtfPromotionBlockVisible: _state => !storage.isExists("__JIFFY__DTF_CTA_CART_CLOSED__"),
    digitalPromotionBlockVisible: state => state.cartState.cartConfig.digitalPromotionBlockVisible,
    dtfCouponApplied: state => state.cartState.cartConfig.dtfCouponApplied,
    madeShippingRule: state => state.cartState.cartConfig.madeShippingRule,
    dtfShippingRule: state => state.cartState.cartConfig.dtfShippingRule,
    digitalShippingRule: state => state.cartState.cartConfig.digitalShippingRule,
    imageServiceShippingRule: state => state.cartState.cartConfig.imageServiceShippingRule,
    spangleShippingRule: state => state.cartState.cartConfig.spangleShippingRule,
    dtfPricing: state => catalogNumber => state.cartState.dtfPricing[catalogNumber],
    lastCompletedOrderPaymentMethodSubtype: state =>
      state.cartState.lastCompletedOrderPaymentMethodSubtype || "custom",
    isRichUIForSavedCarts: state => !state.cartState.platform.isMobile,
    isIncorrectAddress: (state, getters) => {
      // backend validation
      if (state.cartState.cartConfig.isIncorrectAddress) {
        return true;
      }

      // auth user: without saved address and without entered zip
      if (getters.accountPresent && !getters.addressPresent && !getters.citySelected) {
        return true;
      }

      // guest user: without entered zip
      if (!getters.accountPresent && !getters.citySelected) {
        return true;
      }

      // state without delivery
      if (getters.invalidDeliveryState) {
        return true;
      }

      return false;
    },
    orderNumber: state => state.cartState.cartConfig.orderNumber,
    userId: state => state.cartState.cartHeader.clearButtonInformation.userId,
    isEmpty: state => state.cartState.summary.totalItems === 0,
    isNamedCartsAvailable: state => state.cartState.cartConfig.isNamedCartsAvailable,
    isNamedCartsManagementAvailable: state =>
      state.cartState.cartConfig.isNamedCartsManagementAvailable,
    isNamedCartsCustomerAvailable: state =>
      state.cartState.cartConfig.isNamedCartsCustomerAvailable,
    isNamedCartsVisible: state => state.cartState.cartConfig.isNamedCartsVisible,
    isStandardDeliveryInfoVisible: state =>
      state.cartState.cartConfig.isStandardDeliveryInfoVisible,
    updatedAt: state => state.cartState.cartConfig.updatedAt,
    showSaveCartModal: state => state.cartState.cartConfig.showSaveCartModal,
    deliveryWarehouses: state => state.cartState.cartConfig.deliveryWarehouses,
    textForCategory: state => category =>
      state.cartState.cartConfig.shippingTexts[camelize(category)].content,
    modalState: state => state.cartState.modalState,
    experiments: state => state.cartState.experiments,

    // DAM Cross Sell
    damCrossSellOrder: state => state.cartState.damCrossSell.order,
    damCrossSellContent: state => state.cartState.damCrossSell.content,

    // # Addresses
    addressesUrl: state => state.cartState.accountAddresses.addressesUrl,
    shipAddressId: state => state.cartState.accountAddresses.shipAddressId,
    addresses: state => state.cartState.accountAddresses.addresses,
    addressPresent: state => !!state.cartState.accountAddresses.addresses.length,
    completeAddressesPresent: state => state.cartState.accountAddresses.hasCompleteAddresses,
    addressSelected: (state, { addresses = [], shipAddressId }) =>
      addresses.find(({ id }) => id === shipAddressId),

    // # Delivery
    lineItemDeliveryDates: (state, getters) => (groupId, id) => {
      const shipment = getters.selectedShipments[groupId];
      if (!shipment) return [];
      const category = camelize(getters.selectedShipments[groupId].category);
      return state.cartState.delivery.subOrdersDeliveryDates[category][id];
    },
    deliveryDates: state => state.cartState.delivery.deliveryDates,
    deliveryDatesMixed: state => state.cartState.delivery.deliveryDates.mixed,
    deliveryDatesMixedFastest: state => state.cartState.delivery.deliveryDates.mixedFastest,
    hasSameOrNextDayDelivery: (_, getters) =>
      ["sameDay", "nextDay"].some(item => Boolean(getters.deliveryDates[item])),
    deliveryTimeleft: (_, getters) => {
      const selectedShipment = Object.values(getters.selectedShipments)[0];
      if (!selectedShipment) return null;
      return selectedShipment ? selectedShipment.validUntilTime : null;
    },
    minimumDeliveryTimeleft: (state, getters) => {
      const [minTimelelft] = Object.values(state.cartState.shipments.orders)
        .flatMap(order => order.options.map(o => o.validUntilTime))
        .sort((a, b) => new Date(a) - new Date(b));
      return minTimelelft || getters.deliveryTimeleft;
    },
    firstAndLastDeliveryDates: (state, getters) => (groupId, id) => {
      const selectedDeliveryOption = getters.deliveryOptions(groupId).find(o => o.selected);
      if (!selectedDeliveryOption) return 0;

      const selectedDeliverCategory = camelize(selectedDeliveryOption.category);
      const categoryDeliveryDates =
        state.cartState.delivery.subOrdersDeliveryDates[selectedDeliverCategory];
      if (!categoryDeliveryDates) return 0;

      const delivery = categoryDeliveryDates[id];
      if (!delivery) return 0;

      const dates = delivery.dates
        .flatMap(el => [el.deliveryDate, el.paddedDeliveryDate])
        .filter(el => el)
        .sort();
      const firstDate = dates[0];
      const lastDate = dates[dates.length - 1];
      return [new Date(firstDate).getTime(), new Date(lastDate).getTime()];
    },
    changedDeliveryDates: state => state.cartState.delivery.changedDeliveryDates,
    deliveryDatesChanged: state => state.cartState.delivery.deliveryDatesChanged,
    assignmentUid: state => state.cartState.delivery.assignmentUid,
    isLongWait: state => state.cartState.delivery.isLongWait,

    // # Coupon Code
    couponCode: state => state.cartState.couponCode,
    hasCouponApplied: state =>
      parseFloat(state.cartState.summary.couponDiscount) > 0 ||
      parseFloat(state.cartState.couponCode.couponDiscount) > 0 ||
      state.cartState.couponCode.appliedCouponCode ||
      state.cartState.couponCode.freeShipping,

    // # Location
    location: state => state.cartState.location,
    locationAddresses: state => state.cartState.locationAddresses,
    locationLoaded: state => Object.keys(state.cartState.location).length > 0,
    citySelected: state =>
      state.cartState.location.citySelected || state.cartState.location.cityDefault,
    cityDefault: state => state.cartState.location.cityDefault,
    invalidDeliveryState: (state, { locationLoaded, addressSelected }) => {
      // case for autoh user with adress
      // check if user's address is valid
      if (addressSelected) return !addressSelected.stateValid;
      // case for typed zip code
      // check if the state is allowed
      return locationLoaded && !state.cartState.location.stateValid;
    },

    // # Shipments
    shipments: state => state.cartState.shipments,
    freeShipping:
      (_state, { pegasusShipping }) =>
      (id = 0) => {
        const shipping = pegasusShipping(id) || {};
        return !!shipping.freeShipping;
      },
    orderShipmentPrice: (state, getters) =>
      Object.values(getters.selectedShipments).reduce((sum, shipment) => sum + shipment.fee, 0),
    subOrderShipmentPrice: (state, getters) => id => getters.selectedShipments[id]?.fee,
    subOrderSurcharge: (state, getters) => id => getters.selectedShipments[id]?.surcharge,
    subOrderOption: (_state, getters) =>
      Object.values(getters.shipments.orders).reduce(
        (subOrders, subOrder) => ({
          ...subOrders,
          [subOrder.id]: Object.values(subOrder.options).reduce((options, option) => {
            const {
              title,
              deliveryDate,
              transitDays,
              calendarTransitDays,
              shippingMethod: shippingProvider,
              surcharge,
              cost
            } = option;
            return {
              ...options,
              [camelize(option.category)]: {
                title,
                deliveryDate,
                transitDays,
                calendarTransitDays,
                shippingProvider,
                surcharge,
                cost
              }
            };
          }, {})
        }),
        {}
      ),
    mcuOnlySelectedOption: (_, getters) => {
      let option;
      // Prioritize selected option on fgWithNonFastestOption
      option = getters.selectedShipments[getters.fgWithNonFastestOption[0]];
      if (!option) {
        [option, ..._] = Object.values(getters.selectedShipments);
      }
      return option;
    },
    selectedShipments: state =>
      Object.values(state.cartState.shipments.orders).reduce(
        (res, order) => ({
          ...res,
          [order.id]: order.options.find(o => o.selected)
        }),
        {}
      ),
    sortedShipments: state => state.cartState.delivery.sortedShipments,
    sortedSelectedShipments: (state, getters) => {
      const shipments = getters.selectedShipments;
      return getters.sortedShipments.filter(shipment => {
        const { groupId, category } = shipment;

        return !!Object.entries(shipments).find(
          ([curGroupId, option]) => groupId === +curGroupId && category === option.category
        );
      });
    },
    groupedSortedShipments: (_state, getters) =>
      Object.values(getters.sortedShipments).reduce(
        (res, shipment) => ({
          ...res,
          [shipment.groupId]: [...(res[shipment.groupId] || []), shipment]
        }),
        {}
      ),
    deliveryOptions: state => id =>
      state.cartState.shipments.orders[id] ? state.cartState.shipments.orders[id].options : [],
    orderGroups: state => state.cartState.orderGroups,
    orderGroupsSortOrder: (_state, getters) => {
      const experimentVariant = getters.experiments.abBusinessLinesOrderOnCart || 0;
      const assets = getters.digitalShippingRule;
      const transfers = getters.dtfShippingRule;
      const spangles = getters.spangleShippingRule;
      const blanks = "0";

      const order = {
        0: [assets, transfers, spangles, blanks],
        1: [blanks, transfers, spangles, assets],
        2: [blanks, assets, transfers, spangles],
        3: [assets, blanks, transfers, spangles]
      };

      return order[experimentVariant] || order[0];
    },
    shipMethodChangeGroupId: state => state.cartState.shipMethodChangeGroupId,

    // # Shipping
    saleShippingPrices: state => state.cartState.saleImprovedShippingPriceDetails,
    shippingInfo: state => state.cartState.shippingInfo,
    showBlanksFreeShippingBanner: state => state.cartState.showBlanksFreeShippingBanner,
    isEligibleForBlanksFreeShipping: state => state.cartState.isEligibleForBlanksFreeShipping,

    // # Store Credit
    isStoreCreditVisible: state =>
      state.cartState.storeCredit.isVisible && parseFloat(state.cartState.storeCredit.amount) > 0,
    storeCredit: state => state.cartState.storeCredit,
    isSpecificStoreCreditVisible: (state, getters) => businessLine =>
      businessLine === "all"
        ? getters.isStoreCreditVisible
        : state.cartState.specificStoreCredits[businessLine]?.isVisible &&
          parseFloat(state.cartState.specificStoreCredits[businessLine]?.amount || "0.0") > 0,
    specificStoreCredits: state => state.cartState.specificStoreCredits,
    useSpecificStoreCredits: (_state, getters) =>
      Object.fromEntries(
        Object.entries(getters.specificStoreCredits).map(([key, { isUsed }]) => [key, isUsed])
      ),
    expectedTransfersStoreCredit: state => state.cartState.expectedTransfersStoreCredit,
    fullyPayingWithStoreCredits: state => state.cartState.fullyPayingWithStoreCredits,
    transfersCartSubtotal: state => state.cartState.transfersCartSubtotal,
    transfersStoreCreditTiers: state => state.cartState.transfersStoreCreditTiers,

    // # Summary
    cartSummary: state => state.cartState.summary,
    subOrderSummary: state => id => state.cartState.subOrdersSummary[id],
    productsSubtotal: state => parseFloat(state.cartState.summary.productsSubtotal),
    deliveryTotal: (state, getters) => {
      if (!getters.selectedShipments) return 0;

      const reducer = (sum, { fee }) => fee + sum;
      return Object.values(getters.selectedShipments).reduce(reducer, 0);
    },
    surchargeTotal: (state, getters) => {
      if (!getters.selectedShipments) return 0;

      const reducer = (sum, { surcharge }) => surcharge + sum;
      return Object.values(getters.selectedShipments).reduce(reducer, 0);
    },
    cartTotal: (state, getters) =>
      parseFloat(state.cartState.summary.total) +
      getters.shippingTotal +
      getters.taxTotal +
      getters.processingFee,
    netTotal: (_, getters) => {
      const { amount: storeCreditAmount, isUsed: useStoreCredits } = getters.storeCredit;
      let grandTotal = getters.cartTotal;
      Object.values(getters.specificStoreCredits).some(({ isUsed, amount }) => {
        if (isUsed) grandTotal -= parseFloat(amount);

        return grandTotal === 0;
      });
      if (grandTotal > 0 && useStoreCredits) {
        grandTotal -= parseFloat(storeCreditAmount);
      }
      return Math.max(grandTotal, 0);
    },
    cartSubtotalAfterStoreCredits: (state, _) =>
      parseFloat(state.cartState.cartSubtotalAfterStoreCredits || 0),
    taxTotal: state => parseFloat(state.cartState.summary.taxTotal),
    processingFee: (state, getters) => {
      const fee = parseFloat(state.cartState.summary.processingFeeAmount) || 0;
      const total = parseFloat(state.cartState.summary.total) + getters.shippingTotal;
      if (total >= parseFloat(state.cartState.summary.processingFeeThreshold)) {
        return 0;
      }

      return fee;
    },
    shippingTotal: (_, getters) => {
      const { invalidDeliveryState, deliveryTotal, surchargeTotal, freeShippingCouponDiscount } =
        getters;
      let shippingTotal = 0;

      if (!invalidDeliveryState) {
        shippingTotal += parseFloat(deliveryTotal);
        shippingTotal += parseFloat(surchargeTotal);
        if (freeShippingCouponDiscount) shippingTotal -= parseFloat(freeShippingCouponDiscount);
      }

      return shippingTotal;
    },
    freeShippingCouponDiscount: (_, getters) => {
      const { couponCode, subOrderShipmentPrice } = getters;

      if (!couponCode || !couponCode.freeShipping) return 0;

      if (couponCode.freeShippingPromotionType === "single")
        return subOrderShipmentPrice(couponCode.singleFreeShippingSubOrderId);

      return Object.values(getters.selectedShipments).reduce(
        (total, shipment) => total + (shipment.free ? 0 : shipment.strikedShipPrice),
        0
      );
    },
    // # Used by payment method subtypes, i.e. PayPal, Apple Pay, Google Pay, etc.
    currencyCode: state => state.cartState.summary.currencyCode,
    countryCode: state => state.cartState.summary.countryCode,
    // # Out Of Stock
    itemsOutOfStock: state => state.cartState.itemsOutOfStock,
    hasOutOfStock: state => Object.keys(state.cartState.itemsOutOfStock).length > 0,
    itemsTotalOutOfStock: state =>
      Object.values(state.cartState.itemsOutOfStock).reduce((sum, item) => sum + item.missing, 0),
    // # Consolidation
    isParent: state => state.cartState.baseOrders.length > 0,
    childCartsCount: state => state.cartState.baseOrders.length,
    childCartsText: state => pluralize(state.cartState.baseOrders.length, "Cart"),

    // # Unsorted
    pegasusShipping: state => id => state.cartState.subOrdersFreeShipping[id],
    only59FreeShippingThresholds: state =>
      !Object.values(state.cartState.subOrdersFreeShipping).find(
        shipping => shipping.freeShippingThresholdValue !== 59
      ),
    hasJiffyMade: state => state.cartState.subOrdersHasJiffyMade,
    only199FreeShippingThresholds: state =>
      !Object.values(state.cartState.subOrdersFreeShipping).find(
        shipping => shipping.freeShippingThresholdValue !== 199
      ),
    checkoutButtons: state => state.cartState.checkoutButtons,
    androidGpayButtonEnabled: (state, getters) => getters.checkoutButtons.androidGpayButtonEnabled,
    iosApplePayButtonEnabled: (state, getters) => getters.checkoutButtons.iosApplePayButtonEnabled,
    googlePayButtonEnabled: (state, getters) => getters.checkoutButtons.googlePayButtonEnabled,
    googlePayStripeButtonEnabled: (state, getters) =>
      getters.checkoutButtons.googlePayStripeButtonEnabled,
    applePayStripeButtonEnabled: (state, getters) =>
      getters.checkoutButtons.applePayStripeButtonEnabled,
    googlePayLogo: (state, getters) => getters.checkoutButtons.googlePayLogo,
    affirmButtonEnabled: (state, getters) => getters.checkoutButtons.affirmButtonEnabled,
    afterpayButtonEnabled: (state, getters) => getters.checkoutButtons.afterpayButtonEnabled,
    sezzleButtonEnabled: (state, getters) => getters.checkoutButtons.sezzleButtonEnabled,
    nativePaypalCheckoutEnabled: (state, getters) =>
      getters.checkoutButtons.nativePaypalCheckoutEnabled,
    deliveryNotification: (state, getters) => {
      const { category } = getters.isMcuCart
        ? getters.sortedSelectedShipments[0] || {}
        : getters.selectedShipments[0] || {};

      if (category) {
        return state.cartState.deliveryNotification[camelize(category)];
      }

      return (
        state.cartState.deliveryNotification.mixedFastest ||
        state.cartState.deliveryNotification.mixed
      );
    },
    onlyDigitalAssetItems: state => state.cartState.cartConfig.onlyDigitalAssetItems,
    zipcodeState: state => state.cartState.zipcode,
    isCartDisabled: (state, getters) => getters.isIncorrectAddress || getters.hasOutOfStock,
    isCrafterpayOn: state => state.cartState.crafterpayAvailable,
    mobileApplePayVersion: state => state.cartState.mobileApplePayVersion,
    stripeFinancingSupported: state => state.cartState.stripeFinancingSupported,
    showNewCheckoutError: state => state.cartState.showNewCheckoutError,
    showProductSuggestions: state => state.cartState.productSuggestions,
    savedCartsError: state => state.cartState.savedCarts.error,
    savedCartsLoading: state => state.cartState.savedCarts.isLoading,
    savedCartsUpdating: state => state.cartState.savedCarts.isUpdating,
    savedCartsSwitching: state => state.cartState.savedCarts.isSwitching,
    savedCartsCreating: state => state.cartState.savedCarts.isCreating,
    savedCartsList: state => state.cartState.savedCarts.carts,
    savedCartsActive: state => state.cartState.savedCarts.active,
    savedCartsHidden: state => state.cartState.savedCarts.hidden,
    savedCartsLoaded: state => state.cartState.savedCarts.isLoaded,
    savedCartsShowAnnouncement: state => state.cartState.savedCarts.showAnnouncement,
    savedCartsShowDateAnnouncement: state => state.cartState.savedCarts.showDateAnnouncement,
    savedCartsShowDateTooltip: state => state.cartState.savedCarts.showDateTooltip,
    savedCartsShowDateTooltipOnce: state => state.cartState.savedCarts.showDateTooltipOnce,
    savedCartsName: state => state.cartState.cartConfig.cartName,
    currentSavedCart: state => ({
      cart_name: state.cartState.cartConfig.cartName,
      notes: state.cartState.cartConfig.notes,
      customer_purchase_order_number: ""
    }),
    defaultCartName: state => state.cartState.cartConfig.defaultCartName,
    isDefaultCartName:
      (state, getters) =>
      (cartName = getters.savedCartsName) => {
        const regex = new RegExp(`^${getters.defaultCartName}(\\s#\\d+){0,1}$`, "g");
        return regex.test(cartName);
      },
    savedCartsCustomer: state => {
      const customerData = state.cartState.cartConfig.customer;

      if (!isObject(customerData) || !Object.keys(customerData).length) {
        return null;
      }
      return customerData;
    },
    savedCartsIsCustomerAttaching: state => state.cartState.savedCarts.isAttaching,
    savedCartsIsCustomerDetaching: state => state.cartState.savedCarts.isDetaching,
    savedCartsIsCustomerCreating: state => state.cartState.savedCarts.isCustomerCreating,
    savedCartsIsCustomerChoosing: state => state.cartState.savedCarts.isCustomerChoosing,
    guestZipcode: state => state.cartState.cartConfig.guestZipcode,
    isEtsyAutoCart: state => state.cartState.cartConfig.autoCartType !== null,
    hasCrafterpayQuote: state => !!state.cartState.crafterpayQuote.id,
    hasActiveCrafterpayQuote: state => {
      const quoteState = state.cartState.crafterpayQuote.state;
      return quoteState && quoteState !== "deactivated";
    },
    crafterpayQuote: state => state.cartState.crafterpayQuote,
    showEtsyUnmappedItems: (state, getters) =>
      getters.isEtsyAutoCart && state.cartState.etsy.unmappedEtsyLineItemsByListing?.length,
    unmappedEtsyLineItemsByListing: state => state.cartState.etsy.unmappedEtsyLineItemsByListing,
    etsyObservationPeriod: state => state.cartState.etsy.observationPeriod,
    platformName: state => state.cartState.etsy.platformName,
    standardShipmentOption: (state, getters) => category =>
      getters.deliveryOptions(0) &&
      getters.deliveryOptions(0).find(option => camelize(option.category) === camelize(category)),
    standardShippingOptions: (state, getters) =>
      Object.keys(getters.deliveryDates).map(category => getters.standardShipmentOption(category)),
    standardApplePayPayload: (state, getters) =>
      getters.standardShippingOptions.map(option => {
        if (!option) return {};

        const cost =
          getters.couponCode.freeShipping || option.free
            ? option.surcharge
            : option.cost + option.surcharge;
        const deliveryDates = [
          ...new Set(
            getters
              .sortedStandardShippingOptions(option)
              .map(
                shippingOption =>
                  ` - ${
                    shippingOption?.formattedDeliveryDate || shippingOption?.formattedArrivalDate
                  }`
              )
          )
        ];

        return {
          id: option.id,
          identifier: option.category,
          label: option.title || "Shipping",
          detail:
            deliveryDates.length > 0 ? `Estimated Arrival: \n${deliveryDates.join("\n")}` : "",
          amount: cost,
          selected: option.selected
        };
      }),
    standardPaypalShippingOptions: (_, getters) => {
      const shippingOptions = getters.standardShippingOptions.filter(o => o);
      return shippingOptions.map(option => {
        const cost = getters.couponCode.freeShipping || option.free ? 0 : option.cost;

        // build label
        const shippingOptionsDates = [
          ...new Set(
            getters
              .sortedStandardShippingOptions(option)
              .map(
                shippingOption =>
                  shippingOption?.formattedDeliveryDate || shippingOption?.formattedArrivalDate
              )
          )
        ];
        const deliveryDates = [
          ...new Set([
            shippingOptionsDates[0],
            shippingOptionsDates[shippingOptionsDates.length - 1]
          ])
        ];
        let label = `${option.title || "Shipping"} - `;
        label += deliveryDates.length
          ? deliveryDates.map(date => date.split(", ")[1]).join(" - ")
          : deliveryDates[0];

        return {
          type: "SHIPPING",
          id: option.category,
          label,
          selected: option.selected,
          amount: {
            value: cost.toFixed(2),
            currency_code: getters.currencyCode
          }
        };
      });
    },
    sortedStandardShippingOptions: (_state, getters) => option => {
      const { 0: _, ...mcuSelectedShipments } = getters.selectedShipments;
      const mcuShipments = Object.values(mcuSelectedShipments)
        .flat()
        .map(({ shipments }) => shipments)
        .flat();
      const standardShipments =
        getters.deliveryDates[getters.isUs ? camelize(option.category) : "caFastest"];
      const shippingOptions = [...(standardShipments || []), ...(mcuShipments || [])];
      return shippingOptions.sort(
        (a, b) =>
          new Date(a?.date || a?.deprecated?.date) - new Date(b?.date || b?.deprecated?.date)
      );
    },
    mcuShippingCost: (state, getters) => category => {
      const { 0: _, ...mcuShipments } = state.cartState.shipments.orders;
      const total = Object.values(mcuShipments)
        .flat()
        .reduce((res, shipment) => {
          // Find the MCU shipment same as the selected shipping category
          let option = shipment.options.find(o => camelize(o.category) === category);

          // All shipments contain the 'fastest' option.
          // Entering this block means the MCU shipment doesn't offer the selected shipping
          // category. And the customer is most probably considering cost over delivery dates.
          // Hence, we offer the cheapest MCU shipping option possible.
          if (!option) {
            const shipmentOptions = shipment.options;
            option = shipmentOptions[shipmentOptions.length - 1];
          }

          const cost = getters.couponCode.freeShipping || option.free ? 0 : option.cost;
          return res + cost + option.surcharge;
        }, 0);

      return total;
    },
    mcuApplePayPayload: (state, getters) => {
      const mcuShipmentsOnly = !getters.selectedShipments[0];

      if (mcuShipmentsOnly) {
        return Object.keys(getters.deliveryDates).map(category => {
          const mcuShipments = getters.deliveryDates[category];
          const deliveryDates = [
            ...new Set(
              mcuShipments.map(({ formattedDeliveryDate }) => ` - ${formattedDeliveryDate}`)
            )
          ];
          const cost = getters.couponCode.freeShipping ? 0 : getters.mcuShippingCost(category);

          return {
            identifier: category,
            label: getters.shipments.titles[camelize(category)],
            detail:
              mcuShipments.length > 0 ? `Estimated Arrival: \n${deliveryDates.join("\n")}` : "",
            amount: cost,
            selected: category === (getters.mcuOnlySelectedOption?.category || "fastest")
          };
        });
      }

      if (getters.couponCode.freeShippingPromotionType === "all") {
        return getters.standardApplePayPayload;
      }

      return getters.standardApplePayPayload.map(option => ({
        ...option,
        amount: parseFloat(option.amount) + getters.mcuShippingCost(option.identifier)
      }));
    },
    applePayPayload: (_, getters) => {
      const requiredAddressFields = ["email", "name", "phone", "postalAddress"];
      const {
        countryCode,
        currencyCode,
        cartTotal,
        shippingTotal,
        taxTotal,
        isMcuCart,
        mcuApplePayPayload,
        standardApplePayPayload,
        processingFee
      } = getters;

      const { amount: storeCreditAmount, isUsed: useStoreCredits } = getters.storeCredit;

      let lineItems = [
        {
          label: "Item Total",
          type: "final",
          amount: cartTotal - shippingTotal - taxTotal - processingFee
        },
        {
          label: "Shipping",
          type: "final",
          amount: shippingTotal
        },
        {
          label: "Tax Total",
          type: "final",
          amount: taxTotal
        }
      ];
      let total = cartTotal;

      if (processingFee > 0) {
        lineItems = [
          ...lineItems,
          {
            label: "Processing Fee",
            type: "final",
            amount: processingFee
          }
        ];
      }

      Object.values(getters.specificStoreCredits).some(({ isUsed, amount }) => {
        if (isUsed) {
          const amountUsed = Math.min(amount, total);
          lineItems = [
            ...lineItems,
            {
              label: "Store Credit",
              type: "final",
              amount: -amountUsed
            }
          ];

          total -= amountUsed;
        }

        return total === 0;
      });

      if (total > 0 && useStoreCredits) {
        const storeCreditUsed = Math.min(storeCreditAmount, total);
        lineItems = [
          ...lineItems,
          {
            label: "Store Credit",
            type: "final",
            amount: -storeCreditUsed
          }
        ];

        total -= storeCreditUsed;
      }

      const stripeLineItems = lineItems.map(item => ({
        name: item.label,
        amount: Math.round(item.amount * 100)
      }));

      return {
        countryCode,
        currencyCode,
        total: {
          label: "Total",
          type: "final",
          amount: total
        },
        lineItems,
        stripeLineItems,
        stripeTotalAmount: Math.round(total * 100),
        requiredBillingContactFields: requiredAddressFields,
        requiredShippingContactFields: requiredAddressFields,
        shippingMethods: isMcuCart ? mcuApplePayPayload : standardApplePayPayload
      };
    },
    mcuPaypalShippingOptions: (_state, getters) => {
      const mcuShipmentsOnly = !getters.selectedShipments[0];

      if (mcuShipmentsOnly) {
        return Object.keys(getters.deliveryDates).map(category => {
          const mcuShipments = getters.deliveryDates[category];
          const shippingOptionsDates = [
            ...new Set(mcuShipments.map(({ formattedDeliveryDate }) => formattedDeliveryDate))
          ];
          const deliveryDates = [
            ...new Set([
              shippingOptionsDates[0],
              shippingOptionsDates[shippingOptionsDates.length - 1]
            ])
          ];

          let label = `${getters.shipments.titles[camelize(category)] || "Shipping"} - `;
          label +=
            deliveryDates.length > 1
              ? deliveryDates.map(date => date.split(", ")[1]).join(" - ")
              : deliveryDates[0];

          const cost = getters.couponCode.freeShipping ? 0 : getters.mcuShippingCost(category);

          return {
            type: "SHIPPING",
            id: category,
            label,
            selected: category === (getters.mcuOnlySelectedOption?.category || "fastest"),
            amount: {
              value: cost.toFixed(2),
              currency_code: getters.currencyCode
            }
          };
        });
      }

      if (getters.couponCode.freeShippingPromotionType === "all") {
        return getters.standardPaypalShippingOptions;
      }

      // PayPal allows 1 selected shipping option for multi-shipments,
      // aggregate shipments cost per shipping option
      return getters.standardPaypalShippingOptions.map(option => ({
        ...option,
        amount: {
          value: (parseFloat(option.amount.value) + getters.mcuShippingCost(option.id)).toFixed(2),
          currency_code: option.amount.currency_code
        }
      }));
    },
    debugInfo: (state, getters) => {
      const {
        cartState: { isMcuCart, shipments }
      } = state;
      const {
        couponCode,
        deliveryDates,
        paypalShippingOptions,
        selectedShipments,
        standardShippingOptions
      } = getters;

      return {
        shipments_orders: shipments.orders,
        couponCode,
        deliveryDates,
        isMcuCart,
        paypalShippingOptions,
        selectedShipments,
        standardShippingOptions
      };
    },
    paypalShippingOptions: (_, getters) => {
      const { isMcuCart, mcuPaypalShippingOptions, standardPaypalShippingOptions } = getters;

      return isMcuCart ? mcuPaypalShippingOptions : standardPaypalShippingOptions;
    },
    paypalPayload: (state, getters) => {
      const {
        env,
        host,
        addressSelected,
        orderNumber,
        currencyCode,
        cartTotal,
        cartSummary,
        shippingTotal,
        surchargeTotal,
        taxTotal,
        hasCouponApplied,
        couponCode,
        processingFee
      } = getters;

      const { amount: storeCreditAmount, isUsed: useStoreCredits } = getters.storeCredit;
      const finalShippingCost = Math.max(shippingTotal - surchargeTotal, 0);

      let amountTotal = cartTotal;
      let itemTotal = parseFloat(cartSummary.total);
      let discountTotal = 0;

      Object.values(getters.specificStoreCredits).some(({ isUsed, amount }) => {
        if (isUsed) {
          const amountUsed = Math.min(amount, amountTotal);
          amountTotal -= amountUsed;
          discountTotal += amountUsed;
        }

        return amountTotal === 0;
      });

      if (amountTotal > 0 && useStoreCredits) {
        const storeCreditUsed = Math.min(storeCreditAmount, amountTotal);
        amountTotal -= storeCreditUsed;
        discountTotal += storeCreditUsed;
      }

      if (hasCouponApplied && !couponCode.freeShipping) {
        itemTotal += parseFloat(couponCode.couponDiscount);
        discountTotal += parseFloat(couponCode.couponDiscount);
      }

      // Add server name to keep the invoice_id unique even when used in different stagings
      const invoiceID =
        env !== "production"
          ? `${host}-${new Date().toJSON().slice(0, 10)}-${orderNumber}`
          : orderNumber;

      const payload = {
        invoice_id: invoiceID,
        amount: {
          value: String(amountTotal.toFixed(2)),
          currency_code: currencyCode,
          breakdown: {
            item_total: {
              value: String(itemTotal.toFixed(2)),
              currency_code: currencyCode
            },
            tax_total: {
              value: String(taxTotal.toFixed(2)),
              currency_code: currencyCode
            },
            discount: {
              value: String(discountTotal.toFixed(2)),
              currency_code: currencyCode
            },
            shipping: {
              value: String(finalShippingCost.toFixed(2)),
              currency_code: currencyCode
            },
            handling: {
              value: String((surchargeTotal + processingFee).toFixed(2)),
              currency_code: currencyCode
            }
          }
        }
      };

      if (addressSelected) {
        payload.shipping = {
          address: {
            address_line_1: addressSelected.address1,
            address_line_2: addressSelected.address2,
            admin_area_2: addressSelected.city,
            admin_area_1: addressSelected.state,
            postal_code: addressSelected.zipcode,
            country_code: addressSelected.country
          }
        };
      } else {
        payload.shipping = { options: getters.paypalShippingOptions };
      }

      return payload;
    },
    selectedShipmentsProxy: (_, getters) => {
      const { selectedShipments } = getters;
      return {
        summary: {
          fee: Object.values(selectedShipments).reduce((prevFee, { fee }) => prevFee + fee, 0),
          strikedShipPrice: Object.values(selectedShipments).reduce(
            (prevPrice, { strikedShipPrice }) => prevPrice + strikedShipPrice,
            0
          ),
          surcharge: Object.values(selectedShipments).reduce(
            (amount, { surcharge }) => amount + surcharge,
            0
          )
        }
      };
    },
    shipmentsOrdersProxy: (_, getters) => {
      const { orders } = getters.shipments;

      if (Object.values(orders).length) {
        return {
          summary: {
            id: "summary",
            title: "Shipping"
          }
        };
      }

      return orders;
    },
    shippingDiscount: (_, getters) => {
      const { shipmentsOrdersProxy, selectedShipmentsProxy } = getters;
      return Object.values(shipmentsOrdersProxy).reduce((sum, shipOrderProxy) => {
        const { strikedShipPrice, fee } = selectedShipmentsProxy[shipOrderProxy.id];
        const diff = strikedShipPrice - fee;
        if (diff > 0) {
          sum += diff;
        }
        return sum;
      }, 0);
    },
    appPlatform: state => state.cartState.platform.appPlatform,
    host: state => state.cartState.platform.host,
    env: state => state.cartState.platform.env,
    isApp: state => state.cartState.platform.isApp,
    isMobile: state => state.cartState.platform.isMobile,
    uspData: state => state.cartState.usp,
    isAndroidApp: state => state.cartState.platform.appPlatform === "android",
    stripeLineItems: (_state, getters) => category => {
      const { amount: storeCreditAmount, isUsed: useStoreCredits } = getters.storeCredit;
      const { freeShipping, couponDiscount, freeShippingPromotionType } = getters.couponCode;

      const {
        cartTotal,
        shippingTotal,
        cartBulkDiscount,
        hasCouponApplied,
        deliveryTotal,
        shippingDiscount,
        surchargeTotal,
        selectedShipments,
        taxTotal,
        processingFee
      } = getters;

      let lineItems = [
        {
          name: "Product Total",
          amount: Math.round((cartTotal - shippingTotal - taxTotal - processingFee) * 100)
        }
      ];

      if (cartBulkDiscount > 0) {
        lineItems = [
          ...lineItems,
          {
            name: "Bulk Discount",
            amount: Math.round(-cartBulkDiscount * 100)
          }
        ];
      }

      if (processingFee > 0) {
        lineItems = [
          ...lineItems,
          {
            name: "Processing Fee",
            amount: Math.round(processingFee * 100)
          }
        ];
      }

      if (hasCouponApplied && !freeShipping) {
        lineItems = [
          ...lineItems,
          {
            name: "Discount Coupon",
            amount: Math.round(-couponDiscount * 100)
          }
        ];
      }

      const shipTotal = parseFloat(deliveryTotal + shippingDiscount - surchargeTotal);
      lineItems = [
        ...lineItems,
        {
          name: "Shipping",
          amount: Math.round(shipTotal * 100)
        }
      ];

      if (shippingDiscount > 0) {
        lineItems = [
          ...lineItems,
          {
            name: "Shipping Discount",
            amount: Math.round(-shippingDiscount * 100)
          }
        ];
      }

      if (hasCouponApplied && freeShipping) {
        let shippingCouponDiscount = couponDiscount;
        if (category && freeShippingPromotionType === "single") {
          const shipment = Object.values(selectedShipments).find(
            option => camelize(option.category) === category
          );
          shippingCouponDiscount = shipment.strikedShipPrice;
        }
        lineItems = [
          ...lineItems,
          {
            name: "Discount Coupon",
            amount: Math.round(-shippingCouponDiscount * 100)
          }
        ];
      }

      if (surchargeTotal > 0) {
        lineItems = [
          ...lineItems,
          {
            name: "Fuel Surcharge",
            amount: Math.round(surchargeTotal * 100)
          }
        ];
      }

      if (taxTotal > 0) {
        lineItems = [
          ...lineItems,
          {
            name: "Tax Total",
            amount: Math.round(taxTotal * 100)
          }
        ];
      }

      let total = cartTotal;
      Object.values(getters.specificStoreCredits).some(({ isUsed, amount }) => {
        if (isUsed) {
          const amountUsed = Math.min(parseFloat(amount), total);
          lineItems = [
            ...lineItems,
            {
              name: "Store Credits",
              amount: Math.round(-amountUsed * 100)
            }
          ];

          total -= amountUsed;
        }

        return total === 0;
      });

      if (total > 0 && useStoreCredits) {
        const storeCreditUsed = Math.min(storeCreditAmount, total);
        lineItems = [
          ...lineItems,
          {
            name: "Store Credits",
            amount: Math.round(-storeCreditUsed * 100)
          }
        ];
      }

      return lineItems;
    },
    stripeTotal: (_, getters) => {
      let total = getters.cartTotal;
      const { amount: storeCreditAmount, isUsed: useStoreCredits } = getters.storeCredit;
      Object.values(getters.specificStoreCredits).some(({ isUsed, amount }) => {
        if (isUsed) {
          const amountUsed = Math.min(parseFloat(amount), total);
          total -= amountUsed;
        }

        return total === 0;
      });

      if (total > 0 && useStoreCredits) {
        const storeCreditUsed = Math.min(storeCreditAmount, total);
        total -= storeCreditUsed;
      }

      return {
        amount: Math.round(total * 100),
        currency: getters.currencyCode.toLowerCase()
      };
    },
    mcuStripePayload: (_state, getters) => {
      const {
        selectedShipments,
        deliveryDates,
        couponCode,
        mcuShippingCost,
        shipments,
        mcuOnlySelectedOption,
        standardStripePayload
      } = getters;

      const { freeShipping, freeShippingPromotionType } = couponCode;
      const mcuShipmentsOnly = !selectedShipments[0];

      if (mcuShipmentsOnly) {
        return Object.keys(deliveryDates).map(category => {
          const mcuShipments = deliveryDates[category];
          const shippingOptionsDates = [
            ...new Set(mcuShipments.map(({ formattedDeliveryDate }) => formattedDeliveryDate))
          ];
          const dates = [
            ...new Set([
              shippingOptionsDates[0],
              shippingOptionsDates[shippingOptionsDates.length - 1]
            ])
          ];
          const cost = freeShipping ? 0 : mcuShippingCost(category);

          return {
            identifier: camelize(category),
            label: shipments.titles[camelize(category)] || "Shipping",
            detail: mcuShipments.length > 0 ? `Estimated Arrival: \n${dates.join(" - ")}` : "",
            amount: cost,
            surcharge: 0,
            selected: category === (mcuOnlySelectedOption?.category || "fastest"),
            deliveryEstimate:
              mcuShipments.length > 0 ? `Estimated Arrival: \n${dates.join(" - ")}` : ""
          };
        });
      }

      if (freeShippingPromotionType === "all") {
        return standardStripePayload;
      }

      return standardStripePayload.map(option => ({
        ...option,
        amount: parseFloat(option.amount) + mcuShippingCost(option.identifier)
      }));
    },
    standardStripePayload: (_state, getters) => {
      const { standardShippingOptions, couponCode, sortedStandardShippingOptions } = getters;
      const { freeShipping } = couponCode;

      return standardShippingOptions.map(option => {
        if (!option) return {};

        const cost =
          freeShipping || option.free ? option.surcharge : option.cost + option.surcharge;
        const shippingOptionsDates = [
          ...new Set(
            sortedStandardShippingOptions(option).map(
              shippingOption =>
                shippingOption?.formattedDeliveryDate || shippingOption?.formattedArrivalDate
            )
          )
        ];
        const deliveryDates = [
          ...new Set([
            shippingOptionsDates[0],
            shippingOptionsDates[shippingOptionsDates.length - 1]
          ])
        ];

        return {
          identifier: camelize(option.category),
          label: option.title || "Shipping",
          amount: cost,
          cost: option.cost,
          surcharge: option.surcharge,
          selected: option.selected,
          deliveryEstimate:
            deliveryDates.length > 0 ? `Estimated Arrival: ${deliveryDates.join(" - ")}` : ""
        };
      });
    },
    stripeShippingOptions: (_state, getters) => {
      const { isMcuCart, mcuStripePayload, standardStripePayload } = getters;
      const options = isMcuCart ? mcuStripePayload : standardStripePayload;
      return options.filter(option => option && option.identifier);
    },
    stripeShippingRates: (_state, getters) => {
      const { stripeShippingOptions, storeCurrency } = getters;
      const shipOptions = stripeShippingOptions.map(option => {
        let label = "";
        if (option.amount - option.surcharge > 0) {
          const baseAmount = parseFloat(option.amount - option.surcharge);
          label = `${option.label} ${storeCurrency}${baseAmount.toFixed(2)} Flat Rate`;
        } else {
          label = `FREE ${option.label} Shipping`;
        }
        if (option.surcharge > 0) {
          label += ` + ${storeCurrency}${option.surcharge} Fuel Surcharge`;
        }

        return {
          id: option.identifier,
          displayName: label,
          amount: Math.round(option.amount * 100),
          deliveryEstimate: option.deliveryEstimate
        };
      });

      const selectedShippingOption =
        stripeShippingOptions.find(option => option.selected)?.identifier || "standard";

      return shipOptions.sort((a, _b) => {
        if (a.id === selectedShippingOption) {
          return -1;
        }
        return 1;
      });
    },
    changeCartItemStartTime: state => state.cartState.changeCartItemStartTime,
    changeDeliveryDateMixpanelData: (state, getters) => {
      const { changeDeliveryDateMixpanelData = {} } = state.cartState;
      const {
        earliestTimeToGetDate = null,
        earliestTimeToRenderDate = null,
        cartTimeToGetDate = null,
        cartTimeToRenderDate = null
      } = changeDeliveryDateMixpanelData;
      return {
        ...objNamesToSnakeCase(changeDeliveryDateMixpanelData),
        page: getters.isMcuCart ? "mcu-cart" : "cart",
        earliest_time_to_get_date:
          earliestTimeToGetDate !== null ? Math.floor(earliestTimeToGetDate) : null,
        earliest_time_to_render_date:
          earliestTimeToRenderDate !== null ? Math.floor(earliestTimeToRenderDate) : null,
        cart_time_to_get_date: cartTimeToGetDate !== null ? Math.floor(cartTimeToGetDate) : null,
        cart_time_to_render_date:
          cartTimeToRenderDate !== null ? Math.floor(cartTimeToRenderDate) : null,
        zipcode: getters.guestZipcode || getters.addressSelected?.zipcode,
        reason: getters.isLongWait ? "long_waiting" : "cart_update",
        version: 1,
        shipments: state.cartState?.shipments?.groupIds?.length || 0
      };
    },
    // We have 2 vuex store that uses DtfProgressBar. showModal, isRetrying and actionCableConnected is used
    // on the other store but not here. Mocking it to avoid errors.
    showModal: state => state.cartState.dtf.showModal,
    isRetrying: state => state.cartState.dtf.isRetrying,
    actionCableConnected: state => state.cartState.dtf.actionCableConnected,
    preCutConfig: state => state.cartState.preCutConfig,
    reviewsData: state => state.cartState.reviewsData,
    storeCreditsByBusinessLines: state => state.cartState.storeCreditsByBusinessLines,
    showTransfersStoreCreditBanner: state => state.cartState.showTransfersStoreCreditBanner
  },
  // Actions contains all asynchronous operations as ajax calls or Promises.
  actions: {
    prepareToAjax({ commit }) {
      commit("setInitialDeliveryDateMixpanelData", {
        earliestTimeToGetDate: 0,
        cartTimeToGetDate: 0
      });
      commit("setChangeDeliveryDateMixpanelData", {
        earliestTimeToGetDate: 0,
        cartTimeToGetDate: 0
      });
      commit("setCouponLoadingStatus", true);
      commit("setDeliveryLoadingStatus", true);
    },
    setCartResponse({ commit, state }, payload) {
      scrollToAlerts(state.cartState.notifications, objNamesToCamelCase(payload.notifications));
      commit("setCouponLoadingStatus", false);
      commit("setDeliveryLoadingStatus", false);
      commit("replaceCartState", payload);
    },
    getAllDeliveryDates({ dispatch, commit }) {
      dispatch("prepareToAjax");
      const cacheBuster = new Date().getTime();
      const requestStartTime = performance?.now() || 0;
      return api.vueCart.delivery({ cb: cacheBuster }).then(response => {
        const requestEndTime = performance?.now() || 0;
        const requestTotalTime = requestEndTime - requestStartTime;
        commit("setInitialDeliveryDateMixpanelData", {
          earliestTimeToGetDate: requestTotalTime,
          cartTimeToGetDate: requestTotalTime
        });
        commit("setAssignmentDuration", requestTotalTime);
        commit("setChangeDeliveryDateMixpanelData", {
          earliestTimeToGetDate: requestTotalTime,
          cartTimeToGetDate: requestTotalTime
        });
        dispatch("setCartResponse", response);
      });
    },
    getInitialDeliveryDates({ dispatch, commit }) {
      dispatch("prepareToAjax");
      const requestStartTime =
        window?.jiffy?.timers?.cartRenderStartTime || performance?.now() || 0;
      const fallback = () => dispatch("getAllDeliveryDates");
      const initialStatePromise =
        // This global may contain the data with which we may hidrate state
        window?.jiffy?.cart?.initialDeliveryDatesPromise?.catch(() => fallback()) || fallback();
      return initialStatePromise.then(response => {
        const requestEndTime = performance?.now() || 0;
        const requestTotalTime = requestEndTime - requestStartTime;
        commit("setInitialDeliveryDateMixpanelData", {
          earliestTimeToGetDate: requestTotalTime,
          cartTimeToGetDate: requestTotalTime
        });
        commit("setAssignmentDuration", requestTotalTime);
        dispatch("setCartResponse", response);
      });
    },
    removeLineItem({ dispatch, commit }, payload) {
      dispatch("prepareToAjax");
      commit("setRemovingItem", payload.line_item_id);
      return new Promise((resolve, reject) => {
        api.vueCart
          .removeLineItem({ ...payload, quantity: 0 })
          .then(response => {
            const groupsCount = Object.keys(response.order_groups.groups).length;
            if (groupsCount) {
              dispatch("setCartResponse", response);
              dispatch("loadSavedCarts");
              resolve();
              return;
            }

            // reload cart page to show empty banner
            // if there are no products in the cart
            window.location.reload();
          })
          .catch(e => {
            commit("setSystemError", e);
            reject();
          });
      });
    },
    clearOosItems({ commit, getters, dispatch }) {
      const oos = getters.itemsOutOfStock;
      const payload = {
        line_items_attributes: Object.keys(oos).reduce(
          (res, key, index) => ({
            ...res,
            [index]: { id: key, quantity: oos[key].onhand }
          }),
          {}
        )
      };

      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .clearOutOfStockItems(payload)
          .then(response => {
            dispatch("setCartResponse", response);
            resolve();
          })
          .catch(e => {
            commit("setSystemError", e);
            reject();
          });
      });
    },
    updateLineItemQuantity({ dispatch, commit }, payload) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        const requestStartTime = performance?.now() || 0;
        api.vueCart
          .updateLineItem(payload)
          .then(response => {
            const requestEndTime = performance?.now() || 0;
            const requestTotalTime = requestEndTime - requestStartTime;
            commit("setChangeDeliveryDateMixpanelData", {
              earliestTimeToGetDate: requestTotalTime,
              cartTimeToGetDate: requestTotalTime
            });
            const groupsCount = Object.keys(response.order_groups.groups).length;
            if (groupsCount) {
              dispatch("setCartResponse", response);
              dispatch("loadSavedCarts");
              resolve();
              return;
            }

            // reload cart page to show empty banner
            // if there are no products in the cart
            window.location.reload();
          })
          .catch(e => {
            commit("setSystemError", e);
            reject();
          })
          .finally(() => {
            commit("setDeliveryLoadingStatus", false);
          });
      });
    },

    async updateUseStoreCredits({ commit }, value) {
      commit("updateStoreCreditUsageState", value);
      commit("setCheckoutLoadingStatus", true);
      commit("setInventoryLoadingStatus", true);

      try {
        const response = await api.vueCart.setUseStoreCredits({ use_store_credits: value });
        commit("replaceCartState", response);
      } catch (error) {
        window.Honeybadger?.notify("Failed to update store credits usage state", {
          params: {
            error: JSON.stringify(error),
            use_store_credits: value
          }
        });
      } finally {
        commit("setCheckoutLoadingStatus", false);
      }
    },
    async updateUseSpecificStoreCredits({ commit }, { businessLine, isUsed }) {
      commit("updateSpecificStoreCreditUsageState", { businessLine, isUsed });
      commit("setCheckoutLoadingStatus", true);
      commit("setInventoryLoadingStatus", true);

      const underscoredBusinessLine = businessLine
        .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
        .toLowerCase();
      try {
        const response = await api.vueCart.setUseStoreCredits({
          business_line: underscoredBusinessLine,
          use_store_credits: isUsed
        });
        commit("replaceCartState", response);
      } catch (error) {
        window.Honeybadger?.notify("Failed to update store credits usage state", {
          params: {
            error: JSON.stringify(error),
            business_line: businessLine,
            use_store_credits: isUsed
          }
        });
      } finally {
        commit("setCheckoutLoadingStatus", false);
      }
    },

    async getLocation({ state, commit, dispatch }, zipcode) {
      try {
        const data = {
          order_number: state.cartState.cartConfig.orderNumber,
          embroidery_test_order: false,
          zipcode,
          log_not_existing_zipcode: true
        };
        const payload = await api.locations.create(data);

        // Catch error from server
        if (payload.error) throw new PlainError(payload.message);

        if (payload.cities) {
          const [selectedCity] = payload.cities;

          commit("replaceCartState", {
            notifications: [],
            locationAddresses: payload.cities,
            location: {
              city: selectedCity.name,
              stateCode: selectedCity.state_code,
              zipcode: payload.zipcode,
              citySelected: true
            }
          });

          dispatch("putLocation", selectedCity);

          track("location_updated", {
            location: `${selectedCity.name}, ${selectedCity.state_code}, ${payload.zipcode}`
          });
        }

        // fill related places with selected address
        if (payload.session) {
          window.JiffyDeliverTo.updateZipCodeInHeader(payload.session);
        }

        return payload;
      } catch (e) {
        commit("setSystemError", e);
        return null;
      }
    },

    async putLocation({ state, commit, dispatch }, city) {
      try {
        commit("setLocationLoadingStatus", true);
        const { location } = state.cartState;
        const data = {
          zipcode: location.zipcode,
          city_name: {
            name: city.name,
            state_code: city.stateCode
          }
        };
        await api.locations.update({ id: location.zipcode }, data);
        dispatch("getCurrentLocation");
        /**
         * TODO: Investigate the bug. Instead of location.city should be cityName, but cityName doesn't saved on BE.
         *       After reloading the page will be shown the first city from the initial list
         */
        window.JiffyDeliverTo.updateZipCodeInHeader(location);
      } catch (e) {
        commit("setSystemError", e);
        commit("setLocationLoadingStatus", false);
      }
    },

    async getCurrentLocation({ commit, dispatch }) {
      try {
        commit("setLocationLoadingStatus", true);
        const cacheBuster = new Date().getTime();
        const response = await api.locations.getCurrentLocation({ cb: cacheBuster });
        const location = objNamesToCamelCase(response);
        commit("getCurrentLocationSuccess", location);

        if (location.citySelected || location.cityDefault) {
          dispatch("getAllDeliveryDates");
          commit("setGuestZipcode", location.zipcode);
          registerMixpanel({ zipcode: location.zipcode });
        }
      } catch (e) {
        commit("setSystemError", e);
      } finally {
        commit("setLocationLoadingStatus", false);
      }
    },

    changeAddress({ dispatch, commit, state }, { id }) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .setShipAddress({ ship_address_id: id })
          .then(response => {
            commit("changeAddressSuccess", id);
            dispatch("setCartResponse", response);

            const selectedAddress = state.cartState.accountAddresses.addresses.find(
              address => address.id === id
            );

            if (selectedAddress) {
              window.JiffyDeliverTo.updateUserAddressInHeader(selectedAddress.id);
              registerMixpanel({ zipcode: selectedAddress.zipcode });
            }

            resolve();
          })
          .catch(e => {
            commit("setSystemError", e);
            reject();
          });
      });
    },

    updatePaypalErrorData({ commit }, value) {
      commit("setPaypalErrorData", value);
    },

    changeShippingOptions({ commit, getters, dispatch }, category) {
      getters.shipments.groupIds.map(groupId =>
        commit("setSelectedShipmentOption", { category, groupId })
      );
      dispatch("persistSelectedShipments");
    },

    changeShippingOption({ commit, dispatch }, { category, groupId }) {
      commit("setSelectedShipmentOption", { category, groupId });
      commit("setShipMethodChangeGroupId", { groupId });
      dispatch("persistSelectedShipments");
    },

    persistSelectedShipments({ commit, getters, dispatch }) {
      const { selectedShipments } = getters;
      const payload = {
        delivery_option_ids: Object.values(selectedShipments).map(shipment => shipment.id)
      };

      return api.jiffyshirts.orders
        .setShippingSpeed({ order_number: getters.orderNumber }, payload)
        .then(response => {
          dispatch("setCartResponse", response);
        })
        .catch(e => {
          commit("setSystemError", e);
        });
    },

    async updateAddressAndRecalculate({ commit, getters, dispatch }, shippingContact) {
      const payload = {
        address: {
          zipcode: shippingContact?.postalCode,
          city: shippingContact?.locality,
          current: shippingContact?.current || false,
          country_code: shippingContact?.countryCode,
          state_code: shippingContact?.administrativeArea
        },
        use_store_credits: getters.storeCredit.isUsed,
        use_specific_store_credits: getters.useSpecificStoreCredits
      };

      return api.jiffyshirts.orders
        .updateApplePayAddressAndRecalculate({ order_number: getters.orderNumber }, payload)
        .then(response => {
          dispatch("setCartResponse", response);
          return response;
        })
        .catch(e => {
          commit("setSystemError", e);
        });
    },

    updatePaypalOrder({ commit, getters, dispatch }, payload) {
      return api.jiffyshirts.orders
        .updatePaypalSummary({ order_number: getters.orderNumber }, payload)
        .then(response => {
          dispatch("setCartResponse", response);
          return response;
        })
        .catch(e => {
          commit("setSystemError", e);
        });
    },

    async createPaypalOrder({ commit, getters, dispatch }, payload) {
      return api.jiffyshirts.orders
        .createPaypalOrder({ order_number: getters.orderNumber }, payload)
        .then(response => {
          dispatch("setCartResponse", response);
          return response;
        })
        .catch(e => {
          commit("setSystemError", e.toString());
        });
    },

    googleSelectShipping({ commit, getters, dispatch }, payload) {
      return api.jiffyshirts.orders
        .googlePaySummary({ order_number: getters.orderNumber }, payload)
        .then(response => {
          dispatch("setCartResponse", response);
          return response;
        })
        .catch(e => {
          commit("setSystemError", e);
        });
    },
    updatePreCut({ commit, _getters, dispatch }, payload) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .preCut(payload)
          .then(response => {
            if (response.success) {
              dispatch("refreshCartState");
            }
            resolve(response);
          })
          .catch(e => {
            commit("setSystemError", e);
            reject();
          })
          .finally(() => {
            commit("setDeliveryLoadingStatus", false);
          });
      });
    },
    updateArtworkSize({ commit, dispatch }, payload) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .updateDtfSize(payload)
          .then(response => {
            if (response.success) {
              dispatch("refreshCartState");
            }
            resolve(response);
          })
          .catch(e => {
            commit("setSystemError", e);
            reject();
          })
          .finally(() => {
            commit("setDeliveryLoadingStatus", false);
          });
      });
    },
    setCoupon({ commit, dispatch }, couponCode) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .setCouponCode({ coupon_code: couponCode })
          .then(response => {
            dispatch("setCartResponse", response);
            resolve();
          })
          .catch(e => {
            commit("setSystemError", e);
            reject();
          });
      });
    },

    removeCoupon({ commit, dispatch }) {
      dispatch("prepareToAjax");
      return new Promise((resolve, reject) => {
        api.vueCart
          .removeCouponCode()
          .then(response => {
            dispatch("setCartResponse", response);
            resolve();
          })
          .catch(e => {
            commit("setSystemError", e);
            reject();
          });
      });
    },

    refreshCartState({ commit, dispatch }) {
      dispatch("prepareToAjax");
      commit("setRefreshLoading", true);
      commit("setCheckoutLoadingStatus", false);

      const cacheBuster = new Date().getTime();
      const requestStartTime = performance?.now() || 0;
      return new Promise((resolve, reject) => {
        api.vueCart
          .getCartState({ cb: cacheBuster })
          .then(response => {
            const requestEndTime = performance?.now() || 0;
            const requestTotalTime = requestEndTime - requestStartTime;
            commit("setChangeDeliveryDateMixpanelData", {
              earliestTimeToGetDate: requestTotalTime,
              cartTimeToGetDate: requestTotalTime
            });

            const groupsCount = Object.keys(response.order_groups.groups).length;
            if (groupsCount) {
              dispatch("setCartResponse", response);
              resolve();
              return;
            }

            // reload cart page to show empty banner
            // if there are no products in the cart
            window.location.reload();
          })
          .catch(e => {
            commit("setSystemError", e);
            reject();
          })
          .finally(() => {
            commit("setRefreshLoading", false);
          });
      });
    },

    checkCurrentCartActive({ commit, state }) {
      api.jiffyshirts.savedCart.current().then(response => {
        const { active } = state.cartState.savedCarts;
        if (active === null) return;
        if (response?.number !== active.number) {
          scrollToTop();
          commit("setSavedCartsError", null);
          commit("setSavedCartsSwitching", true);
          commit("pushNotification", {
            type: "warning",
            text: "Your cart has been changed. Updating your cart..."
          });

          window.location.reload();
        }
      });
    },

    checkCartItems({ commit, getters, state }) {
      api.vueCart.currentItems().then(response => {
        if (lineItemsChanged(response.items, state.cartState.orderGroups.groups)) {
          scrollToTop();
          track("multitab_browsing", {
            order_number: getters.orderNumber,
            item_count: state.cartState.summary.totalItems
          });
          commit("pushNotification", {
            type: "warning",
            text: "Your cart items are changed. Updating your cart..."
          });

          window.location.reload();
        }
      });

      function lineItemsChanged(latestLineItems, pageLineItems) {
        if (Object.keys(latestLineItems).length === 0) {
          return true;
        }

        const combinedLineItems = Object.values(pageLineItems).flatMap(lineItemGroup =>
          lineItemGroup.lineItems ? Object.values(lineItemGroup.lineItems) : []
        );

        const pageLineItemsMap = combinedLineItems.reduce((map, item) => {
          map[item.id] = item.quantity;
          return map;
        }, {});

        return Object.keys(latestLineItems).some(
          id => pageLineItemsMap[id] !== latestLineItems[id]
        );
      }
    },

    proceedCheckout({ commit }) {
      // let the `SubmitEvent` occur
      setTimeout(() => {
        commit("setCheckoutLoadingStatus", true);
        commit("setInventoryLoadingStatus", true);
      }, 0);
    },

    setCheckoutLoadingStatus({ commit }, status) {
      commit("setCheckoutLoadingStatus", status);

      // initially always show inventory loader
      if (status) {
        commit("setInventoryLoadingStatus", status);
      }
    },

    setInventoryLoadingStatus({ commit }, status) {
      commit("setInventoryLoadingStatus", status);
    },

    async redirectToEditCrafterpayQuote({ state, getters, commit }) {
      commit("setCheckoutLoadingStatus", true);
      commit("setInventoryLoadingStatus", true);

      try {
        let response;
        response = await api.crafterpay.crafters.createQuote();

        const quoteId = response.quote.id;
        commit("setIsCrafterOnboarded", response.is_onboarded);
        track("crafterpay.create_invoice_button.clicked", { quoteId });

        response = await api.crafterpay.crafters.updateQuoteShippingCost(
          { quote_id: quoteId },
          { shipping_cost: getters.deliveryTotal }
        );
        if (response.success === true) {
          let url;
          const onboarded = state.cartState.cartConfig.isCrafterOnboarded;
          if (onboarded) {
            const editQuotePath = resolveOptions(routes.crafterpayEditQuotePath(), false, {
              quote_id: quoteId
            });
            url = editQuotePath.url;
          } else {
            url = routes.crafterpayOnboardingPath();
          }
          window.location.href = url;
        }
      } catch (e) {
        commit("setCheckoutLoadingStatus", false);
        commit("setSystemError", e);
      }
    },

    async loadSavedCarts({ commit, getters }) {
      if (!getters.isNamedCartsAvailable) return;

      try {
        commit("setSavedCartsLoading", true);
        let carts;
        let active;

        if (getters.isNamedCartsManagementAvailable) {
          [{ carts }, active] = await Promise.all([
            api.jiffyshirts.savedCart.list(),
            api.jiffyshirts.savedCart.current()
          ]);
        } else {
          active = await api.jiffyshirts.savedCart.current();
          carts = [active];
        }
        commit("setSavedCarts", { carts, active });
      } catch (error) {
        commit("setSavedCartsError", error.message);
      } finally {
        commit("setSavedCartsLoading", false);
      }
    },

    async handleCartUpdate({ commit, getters }, cart) {
      try {
        commit("setSavedCartsError", null);
        commit("setSavedCartsUpdating", true);
        const id = cart.number || getters.savedCartsActive.number;
        await api.jiffyshirts.savedCart.update({ id }, { saved_carts: cart });
        commit("setSavedCartsUpdatedAt", new Date().toString());

        if (getters.isNamedCartsManagementAvailable) {
          const { carts } = await api.jiffyshirts.savedCart.list();
          commit("setSavedCarts", { carts });
          commit("setSavedCartsName", carts?.[0]?.cart_name);
        } else {
          const active = await api.jiffyshirts.savedCart.current();
          const carts = [active];

          commit("setSavedCarts", { carts });
          commit("setSavedCartsName", carts?.[0]?.cart_name);
        }
        commit("setSavedCartsUpdating", false);
      } catch (error) {
        const ignoreErrors = ["NetworkError when attempting to fetch resource."];
        if (!ignoreErrors.includes(error.message)) {
          commit("setSavedCartsError", error.message);
        }
      } finally {
        commit("setSavedCartsUpdating", false);
      }
    },
    async handleCartChange({ commit }, cart) {
      try {
        commit("setSavedCartsError", null);
        commit("setSavedCartsSwitching", true);
        await api.jiffyshirts.savedCart.switch({ id: cart.number });
        window.location.reload();
      } catch (error) {
        commit("setSavedCartsError", error.message);
        commit("setSavedCartsSwitching", false);
      }
    },
    async handleCartCreate({ commit }, cart) {
      try {
        commit("setSavedCartsError", null);
        commit("setSavedCartsCreating", true);
        await api.jiffyshirts.savedCart.create({ saved_carts: cart });
        window.location.reload();
      } catch (error) {
        commit("setSavedCartsError", error.message);
      } finally {
        commit("setSavedCartsCreating", false);
      }
    },
    hideSavedCartsAnnouncement({ commit }) {
      commit("setSavedCartsAnnouncement", false);
      flag.toggle("__JIFFY__VUE__MULTI_CARTS_ANNOUNCEMENT__");
    },
    hideSavedCartsDateAnnouncement({ commit }) {
      commit("setSavedCartsDateAnnouncement", false);
      flag.toggle("__JIFFY__VUE__MULTI_CARTS_DATE_ANNOUNCEMENT__");
    },
    toggleSavedCartsDateTooltip({ commit }, visible) {
      commit("setSavedCartsDateTooltip", visible);

      if (!visible) {
        commit("setSavedCartsDateTooltipOnce");
        flag.toggle("__JIFFY__VUE__MULTI_CARTS_DATE_TOOLTIP__");
      }
    },
    updateSavedCartsName({ commit }, name) {
      commit("setSavedCartsName", name);
      changeActiveCartName(name);
    },
    async attachCartCustomer({ commit, getters }, customer) {
      try {
        commit("setSavedCartsError", null);
        commit("setSavedCartsCustomerAttaching", true);
        await api.consumers.attachToOrder(
          { orderId: getters.orderNumber },
          { consumer_guid: customer.guid }
        );
        commit("setSavedCartsCustomer", customer);
      } catch (error) {
        commit("setSavedCartsError", error.message);
      } finally {
        commit("setSavedCartsCustomerAttaching", false);
      }
    },
    async detachCartCustomer({ commit, getters }) {
      try {
        commit("setSavedCartsError", null);
        commit("setSavedCartsCustomerDetaching", true);
        await api.consumers.detachFromOrder({ orderId: getters.orderNumber });
        commit("setSavedCartsCustomer", null);
      } catch (error) {
        commit("setSavedCartsError", error.message);
      } finally {
        commit("setSavedCartsCustomerDetaching", false);
      }
    },
    async createCartCustomer({ commit, getters }, customer) {
      try {
        commit("setSavedCartsCustomerCreating", true);
        const { consumer, errors } = await api.consumers.create({
          consumer: customer,
          order_number: getters.orderNumber
        });
        commit("setSavedCartsCustomerCreating", false);
        if (consumer) {
          commit("setSavedCartsCustomer", objNamesToCamelCase(consumer));
        }
        return { consumer, errors };
      } catch (error) {
        commit("setSavedCartsCustomerCreating", false);
        throw error;
      }
    },
    async obtainEtsyLineItemsByListing({ commit, getters }) {
      api.merchantListings
        .unmappedLineItems({ order_number: getters.orderNumber })
        .then(response => {
          if (response.success) {
            commit("setEtsyObservationPeriod", response.period);
            commit("setPlatformName", response.platform_name);
            commit("setUnmappedEtsyLineItemsByListing", response.listings);
            return;
          }

          commit("setUnmappedEtsyLineItemsByListing", []);
        })
        .catch(() => {
          commit("setUnmappedEtsyLineItemsByListing", []);
        });
    },
    async submitApplePayToken(_, { nonce, payment }) {
      const { shippingContact, billingContact } = payment;

      const shippingAddress = {
        addressLine: shippingContact.addressLines,
        country: shippingContact.countryCode.toUpperCase(),
        postalCode: shippingContact.postalCode,
        recipient: `${shippingContact.givenName} ${shippingContact.familyName}`,
        region: shippingContact.administrativeArea,
        city: shippingContact.locality,
        phone: shippingContact.phoneNumber
      };

      const card = {
        name: `${billingContact.givenName} ${billingContact.familyName}`,
        address_city: billingContact.locality,
        address_country: billingContact.countryCode.toUpperCase(),
        address_line1: billingContact.addressLines[0],
        address_line2: billingContact.addressLines[1],
        address_state: billingContact.administrativeArea,
        address_zip: billingContact.postalCode
      };

      const payload = {
        checkout_no_confirmation: true,
        shipping: {
          fullname: `${shippingContact.givenName} ${shippingContact.familyName}`
        },
        billing: {
          fullname: `${billingContact.givenName} ${billingContact.familyName}`
        },
        order: {
          ship_address_attributes: {},
          bill_address_attributes: {}
        },
        payment_method_subtype: "apple-pay",
        payment_token: nonce,
        state: "payment",
        payment_method: "device_pay",
        payment_type: "device_pay",
        checkout: true,
        // Apple pay device data
        "device-pay-token": JSON.stringify({
          id: nonce,
          email: billingContact.emailAddress || shippingContact.emailAddress,
          phone: billingContact.phoneNumber || shippingContact.phoneNumber,
          card,
          shippingAddress,
          type: "card",
          paymentMethod: "apple-pay"
        })
      };

      api.jiffyshirts.checkout.submitPayment(payload).then(response => {
        if (response?.redirect_to) {
          window.location.href = response.redirect_to;
        } else {
          window.location.href = "/cart";
        }
      });
    },

    hardRefresh({ commit, dispatch }) {
      dispatch("prepareToAjax");
      commit("setRefreshLoading", true);
      window.location.reload();
    },
    async stripeCreatePaymentIntent(
      { dispatch },
      { stripe, elements, paymentMethod, paymentData }
    ) {
      try {
        const response = await api.jiffyshirts.checkout.stripeCreateIntent(paymentData);
        const { error: backendError, redirect_to: redirectUrl } = response;

        if (redirectUrl) {
          redirectTo(redirectUrl);
        } else if (backendError) {
          throw backendError;
        }

        elements.update({ customerSessionClientSecret: response.client_secret });
        await dispatch("stripeConfirmPayment", {
          stripe,
          elements,
          clientSecret: response.client_secret,
          returnUrl: response.return_url
        });
      } catch (error) {
        dispatch("expressCheckoutHandleError", { error, paymentMethod });
      } finally {
        document.body.classList.remove("processing-visible");
      }
    },

    async stripeConfirmPayment(_, { stripe, elements, clientSecret, returnUrl }) {
      const response = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: returnUrl },
        redirect: "always"
      });

      if (response.error) {
        throw response.error;
      }

      return response;
    },
    expressCheckoutHandleError(
      { dispatch, getters },
      { error, paymentMethod, redirectUrl = null }
    ) {
      dispatch("logCheckoutData", { error, kind: `${paymentMethod}.checkout_error` });

      window.jiffy.ui.cart.form.loading = false;
      document.body.classList.remove("processing-visible");

      dispatch("resetDeliveryAddress");
      dispatch("raiseErrorTracking", { error, paymentMethod });
      const cartUrl = getters.isUs ? "/cart" : "/ca/cart";
      redirectTo(redirectUrl || cartUrl);
    },
    logCheckoutData(payload, kind) {
      const loggerPayload = { order_number: window.jiffy.orderNumber, data: payload, kind };

      try {
        api.jiffyshirts.checkout.log(loggerPayload);
        // eslint-disable-next-line no-empty
      } catch (e) {}
    },
    resetDeliveryAddress({ commit, dispatch, getters }) {
      const { addressSelected, addresses } = getters;
      const origAddress = addressSelected || addresses[0];
      if (origAddress) {
        commit("changeAddress", { id: origAddress.id });
      } else {
        dispatch("getAllDeliveryDates");
      }
      if (window.jiffy?.ui?.cart?.form) {
        window.jiffy.ui.cart.form.loading = false;
      }
    },
    raiseErrorTracking(error, paymentMethod) {
      if (window.Honeybadger) {
        window.Honeybadger.notify(`${paymentMethod} Checkout Error`, {
          params: { error: JSON.stringify(error) }
        });
      }

      track("checkout_error", {
        error_message: JSON.stringify(error),
        payment_method: paymentMethod,
        error_details: error
      });
    },

    /* eslint-disable no-unused-vars */
    sendDeliveryDateMixpanelData({ commit, state, getters }, partialMixpanelData) {
      commit("setInitialDeliveryDateMixpanelData", partialMixpanelData);

      const { initialDeliveryDateMixpanelData } = state.cartState;
      const { isSent = false, ...eventProps } = initialDeliveryDateMixpanelData || {};
      if (isSent) return;

      // When the data is gathered, fire the event
      const isDataComplete = Object.values(eventProps).every(value => value !== null);
      if (isDataComplete) {
        // track("delivery_dates_rendered", {
        //   page: getters.isMcuCart ? "mcu-cart" : "cart",
        //   earliest_time_to_get_date: Math.floor(eventProps?.earliestTimeToGetDate),
        //   earliest_time_to_render_date: Math.floor(eventProps?.earliestTimeToRenderDate),
        //   cart_time_to_get_date: Math.floor(eventProps?.cartTimeToGetDate),
        //   cart_time_to_render_date: Math.floor(eventProps?.cartTimeToRenderDate),
        //   shipments: state.cartState?.shipments?.groupIds?.length || 0
        // });
        // commit("setInitialDeliveryDateMixpanelData", { isSent: true });
        // TODO: cleanup code related to delivery_dates_rendered calculations
        // Cart: https://github.com/sdtechdev/spree-jiffyshirts/pull/33466 , https://github.com/sdtechdev/spree-jiffyshirts/pull/33581
        // PDP: https://github.com/sdtechdev/spree-jiffyshirts/pull/33379
        // PLP: https://github.com/sdtechdev/spree-jiffyshirts/pull/33623
      }
    },

    sendChangeDeliveryDateMixpanelData({ commit, _state, getters }, partialMixpanelData) {
      commit("setChangeDeliveryDateMixpanelData", partialMixpanelData);
      const { changeDeliveryDateMixpanelData = {} } = getters;
      const isDataComplete = Object.values(changeDeliveryDateMixpanelData).every(
        value => value !== null
      );

      // When the data is gathered, fire the event
      if (getters.deliveryDatesChanged && isDataComplete) {
        track("delivery_dates_changed", changeDeliveryDateMixpanelData);
        // Commit initial state to start gathering the data again
        commit("setChangeDeliveryDateMixpanelData", initialState.changeDeliveryDateMixpanelData);
        commit("setChangeCartItemStartTime", 0);
      }
    },

    async setEnhancementPreferences(_, { id, requestNote, callback, event }) {
      try {
        callback("saving");
        const apiParams = {
          dtf_artwork: {
            enhancement_request_note: requestNote,
            enhance_image: true
          }
        };
        await api.dtf.updateArtworkRedrawData({ id }, apiParams);
        submitDtfEvent("designer_notes_cart", {
          artwork_id: id,
          note: requestNote,
          button_pressed: event
        });
        callback("view");
      } catch (error) {
        window.Honeybadger?.notify("Artwork enhancement setting save from cart failed", {
          params: { error_message: error.message }
        });
      }
    }
  },
  // plugins
  plugins,
  modules: {
    digitalAssets: digitalAssetsModule
  }
});

export default store;
